/**
 * Aug-2026 (User-Wunsch Jörg) — Öffentliche Handy-Upload-Seite des
 * Dokumenten-Postausgangs (Route /m/outbox/:token, KEIN Login nötig).
 * Der Filial-QR auf der Postausgangs-Seite führt hierher: Beleg fotografieren
 * oder PDF wählen → gleiche KI-Verarbeitung wie der überwachte Desktop-Ordner.
 */
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, FileUp, Loader2, CheckCircle2, XCircle, FolderOpen, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MobileOutboxUploadPage() {
    const { token } = useParams();
    const [state, setState] = useState("loading"); // loading | ready | invalid
    const [branch, setBranch] = useState("");
    const [uploads, setUploads] = useState([]); // {id, name, status: uploading|pending|done|error, error}
    const camRef = useRef(null);
    const fileRef = useRef(null);

    useEffect(() => {
        axios.get(`${API}/public/outbox-mobile/${token}`)
            .then((r) => { setBranch(r.data.branch_label || ""); setState("ready"); })
            .catch(() => setState("invalid"));
    }, [token]);

    // Job-Status pollen, bis fertig/Fehler
    useEffect(() => {
        const open = uploads.filter((u) => u.status === "pending" && u.jobId);
        if (!open.length) return undefined;
        const t = setInterval(async () => {
            for (const u of open) {
                try {
                    const { data } = await axios.get(`${API}/public/outbox-mobile/${token}/jobs/${u.jobId}`);
                    if (data.status === "done" || data.status === "error") {
                        setUploads((prev) => prev.map((x) => x.id === u.id
                            ? { ...x, status: data.status, error: data.error } : x));
                    }
                } catch { /* transient */ }
            }
        }, 3000);
        return () => clearInterval(t);
    }, [uploads, token]);

    const handleFiles = async (files) => {
        for (const file of Array.from(files || [])) {
            const id = `${Date.now()}-${Math.random()}`;
            setUploads((prev) => [{ id, name: file.name || "Foto", status: "uploading" }, ...prev]);
            const fd = new FormData();
            fd.append("file", file, file.name || `beleg-${Date.now()}.jpg`);
            try {
                const { data } = await axios.post(`${API}/public/outbox-mobile/${token}/upload`, fd);
                setUploads((prev) => prev.map((x) => x.id === id
                    ? { ...x, status: "pending", jobId: data.job_id } : x));
            } catch (e) {
                setUploads((prev) => prev.map((x) => x.id === id
                    ? { ...x, status: "error", error: e.response?.data?.detail || "Upload fehlgeschlagen" } : x));
            }
        }
    };

    if (state === "loading") {
        return <div className="min-h-screen bg-[#111827] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#FFCC00] animate-spin" /></div>;
    }
    if (state === "invalid") {
        return (
            <div className="min-h-screen bg-[#111827] flex items-center justify-center p-6 text-center">
                <div className="text-white space-y-2" data-testid="mobile-outbox-invalid">
                    <XCircle className="w-10 h-10 text-red-400 mx-auto" />
                    <p className="font-bold">QR-Code ungültig oder deaktiviert.</p>
                    <p className="text-sm text-gray-400">Bitte den QR-Code am Arbeitsplatz neu scannen.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#111827] text-white pb-16" data-testid="mobile-outbox-page">
            <div className="px-5 pt-8 pb-5 border-b border-gray-700">
                <div className="flex items-center gap-2 text-[#FFCC00] font-display font-black text-xl">
                    <FolderOpen className="w-6 h-6" /> Beleg-Upload
                </div>
                <p className="text-sm text-gray-300 mt-1">
                    Beleg fotografieren oder PDF wählen — die KI ordnet das Dokument automatisch
                    Fahrzeug &amp; Kunde zu. Ergebnis erscheint am Desktop im Postausgang.
                </p>
                {branch && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-gray-300 bg-gray-800 border border-gray-700 rounded-sm px-2 py-1" data-testid="mobile-outbox-branch">
                        <MapPin className="w-3 h-3 text-emerald-400" /> Belege gehen an: <b className="text-white">{branch}</b>
                    </div>
                )}
            </div>

            <div className="px-5 py-6 space-y-3">
                <button type="button" onClick={() => camRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 bg-[#FFCC00] text-[#111827] font-black uppercase tracking-wider rounded-sm py-4 text-sm"
                    data-testid="mobile-outbox-camera-btn">
                    <Camera className="w-5 h-5" /> Beleg fotografieren
                </button>
                <button type="button" onClick={() => fileRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 bg-gray-800 border border-gray-600 text-white font-bold uppercase tracking-wider rounded-sm py-3.5 text-sm"
                    data-testid="mobile-outbox-file-btn">
                    <FileUp className="w-5 h-5" /> PDF / Datei wählen
                </button>
                <input ref={camRef} type="file" accept="image/*" capture="environment" multiple hidden
                    onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
                <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple hidden
                    onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />

                {uploads.length > 0 && (
                    <ul className="space-y-2 pt-2" data-testid="mobile-outbox-uploads">
                        {uploads.map((u) => (
                            <li key={u.id} className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-sm px-3 py-2.5 text-sm">
                                {(u.status === "uploading" || u.status === "pending") && <Loader2 className="w-4 h-4 text-[#FFCC00] animate-spin shrink-0" />}
                                {u.status === "done" && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />}
                                {u.status === "error" && <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
                                <span className="truncate flex-1">{u.name}</span>
                                <span className="text-[10px] uppercase tracking-wider text-gray-400 shrink-0">
                                    {u.status === "uploading" ? "lädt hoch…"
                                        : u.status === "pending" ? "KI analysiert…"
                                            : u.status === "done" ? "übermittelt"
                                                : (u.error || "Fehler")}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
                <p className="text-[11px] text-gray-500 pt-1">
                    Mehrere Belege nacheinander möglich. Diese Seite kann geschlossen werden,
                    sobald alle Uploads als übermittelt angezeigt werden.
                </p>
            </div>
        </div>
    );
}
